import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { AREA, areaPresets, areaTier, budgets, stepLabels, timelines, typologies } from '../../data/contact';
import { useFormSubmit } from '../../lib/forms';
import { SITE } from '../../lib/site';
import { formatInt } from '../../lib/ui';
import { AppLink } from '../AppLink';
import { FormStatusMessage } from '../FormStatusMessage';
import { Honeypot } from '../Honeypot';
import { Icon } from '../Icon';

const STEPS = stepLabels.length;

const field =
  'w-full bg-surface-cream rounded px-space-md py-space-sm font-body-md text-body-md text-on-surface placeholder:text-outline focus:outline-none focus:ring-2 focus:ring-secondary/40 shadow-inner';
const fieldLabel = 'font-label-md text-label-md uppercase tracking-wider text-primary font-bold';
const legendCls = 'font-label-sm text-label-sm uppercase tracking-wider text-primary font-bold mb-space-xs';
const nextBtn =
  'inline-flex items-center justify-center gap-space-xs bg-primary text-gold-light hover:bg-primary-container px-space-lg py-space-sm rounded font-label-lg text-label-lg uppercase tracking-wider transition-all duration-200 shadow-md text-center';
const prevBtn =
  'inline-flex items-center gap-space-xs text-primary hover:text-secondary font-label-lg text-label-lg uppercase tracking-wider transition-colors';
// Styles for the card next to a visually hidden radio (input.peer + div).
const choiceCard =
  'rounded bg-surface-cream shadow-sm transition-all duration-200 peer-checked:bg-primary peer-checked:text-on-primary peer-focus-visible:ring-2 peer-focus-visible:ring-secondary peer-focus-visible:ring-offset-2 peer-checked:[&_.ic]:text-gold-light peer-checked:[&_.sub]:text-primary-fixed-dim peer-checked:[&_.dot]:bg-gold-light peer-checked:[&_.dot-inner]:opacity-100';

function Choice({
  name,
  value,
  defaultChecked,
  className = '',
  cardClassName = '',
  children,
}: {
  name: string;
  value: string;
  defaultChecked?: boolean;
  className?: string;
  cardClassName?: string;
  children: ReactNode;
}) {
  return (
    <label className={`cursor-pointer group ${className}`}>
      <input className="peer sr-only" type="radio" name={name} value={value} defaultChecked={defaultChecked} />
      <div className={`${choiceCard} ${cardClassName}`}>{children}</div>
    </label>
  );
}

/** Step heading: focus target when the visitor moves between steps. */
function StepHeading({ eyebrow, title, text, headingRef }: { eyebrow: string; title: string; text: string; headingRef?: (el: HTMLHeadingElement | null) => void }) {
  return (
    <div className="flex flex-col">
      <span className="font-label-sm text-label-sm uppercase tracking-widest text-on-surface-variant">{eyebrow}</span>
      <h3 ref={headingRef} tabIndex={-1} className="font-headline-md text-headline-md text-primary focus:outline-none">
        {title}
      </h3>
      <p className="font-body-sm text-body-sm text-on-surface-variant">{text}</p>
    </div>
  );
}

/** Multi-step "private scoping" consultation form on the contact page. */
export function ScopingForm() {
  const { status, error, onSubmit, reset } = useFormSubmit('contact');
  const [step, setStep] = useState(1);
  const [area, setArea] = useState(AREA.initial);
  const id = useId();
  const stepEls = useRef<Array<HTMLDivElement | null>>([]);
  const headings = useRef<Array<HTMLHeadingElement | null>>([]);
  const successRef = useRef<HTMLDivElement | null>(null);
  const moved = useRef(false);
  const done = status === 'success';

  // Move focus to the new step's heading after navigation (not on first render).
  useEffect(() => {
    if (!moved.current) return;
    headings.current[step - 1]?.focus({ preventScroll: true });
    stepEls.current[step - 1]?.closest('[data-scoping-card]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [step]);

  useEffect(() => {
    if (done) successRef.current?.focus();
  }, [done]);

  function goTo(next: number) {
    if (next > step) {
      const current = stepEls.current[step - 1];
      const fields = current?.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>('input, textarea, select');
      for (const f of fields ?? []) {
        if (!f.checkValidity()) {
          f.reportValidity();
          return;
        }
      }
    }
    moved.current = true;
    setStep(next);
  }

  function startOver() {
    reset();
    setArea(AREA.initial);
    moved.current = true;
    setStep(1);
  }

  const setStepRef = (i: number) => (el: HTMLDivElement | null) => {
    stepEls.current[i] = el;
  };
  const setHeadingRef = (i: number) => (el: HTMLHeadingElement | null) => {
    headings.current[i] = el;
  };
  const percent = done ? 100 : Math.round((step / STEPS) * 100);
  const whatsappPlan = `${SITE.whatsappUrl}?text=${encodeURIComponent(
    'Hello Green Valley Interiors, I have just sent a project brief and would like to share my floor plan.',
  )}`;

  return (
    <div data-scoping-card className="bg-surface-ivory rounded shadow-xl p-space-md sm:p-space-lg lg:p-space-xl relative scroll-mt-24">
      <h2 className="sr-only">Request a private design consultation</h2>

      <div className="mb-space-lg">
        <div className="flex flex-wrap items-center justify-between gap-x-space-sm gap-y-1 mb-space-xs">
          <p className="font-label-md text-label-md uppercase tracking-wider text-secondary font-bold" aria-live="polite">
            {done ? 'Scoping brief submitted' : `Step ${step} of ${STEPS}: ${stepLabels[step - 1]}`}
          </p>
          <span className="font-body-sm text-body-sm text-on-surface-variant">{percent}% Configured</span>
        </div>
        <div className="w-full h-1 bg-surface-cream rounded-full overflow-hidden flex" aria-hidden="true">
          <div className="h-full bg-primary transition-all duration-500" style={{ width: `${percent}%` }} />
        </div>
      </div>

      <form className="relative flex flex-col gap-space-lg" onSubmit={onSubmit}>
        <Honeypot />

        {/* STEP 1 — typology */}
        <div ref={setStepRef(0)} hidden={done || step !== 1} className="flex flex-col gap-space-md">
          <StepHeading
            headingRef={setHeadingRef(0)}
            eyebrow="Classification"
            title="Select Spatial Typology"
            text="Define the architectural program that best encapsulates your assignment."
          />
          <fieldset>
            <legend className="sr-only">Spatial typology</legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-space-sm">
              {typologies.map((t, i) =>
                t.wide ? (
                  <Choice
                    key={t.value}
                    name="typology"
                    value={t.value}
                    defaultChecked={i === 0}
                    className="sm:col-span-2"
                    cardClassName="p-space-md flex flex-col sm:flex-row sm:items-center sm:justify-between gap-space-xs group-hover:-translate-y-0.5"
                  >
                    <div className="flex items-center gap-space-sm">
                      <Icon name={t.icon} size={24} className="ic text-secondary" />
                      <div>
                        <span className="font-headline-sm text-[18px] leading-7 block">{t.title}</span>
                        <span className="font-body-sm text-body-sm opacity-80">{t.text}</span>
                      </div>
                    </div>
                    {t.badge && (
                      <span className="self-start sm:self-auto font-label-sm text-label-sm uppercase px-2 py-1 rounded bg-surface-ivory text-primary font-bold">
                        {t.badge}
                      </span>
                    )}
                  </Choice>
                ) : (
                  <Choice
                    key={t.value}
                    name="typology"
                    value={t.value}
                    defaultChecked={i === 0}
                    cardClassName="p-space-md flex flex-col gap-space-xs h-full group-hover:-translate-y-0.5"
                  >
                    <div className="flex items-center justify-between">
                      <Icon name={t.icon} size={24} className="ic text-secondary" />
                      <span className="dot w-4 h-4 rounded-full bg-surface-ivory flex items-center justify-center">
                        <span className="dot-inner w-2 h-2 rounded-full bg-primary opacity-0" />
                      </span>
                    </div>
                    <span className="font-headline-sm text-[18px] leading-7">{t.title}</span>
                    <span className="font-body-sm text-body-sm opacity-80">{t.text}</span>
                  </Choice>
                ),
              )}
            </div>
          </fieldset>
          <div className="flex items-center justify-end pt-space-sm">
            <button type="button" className={nextBtn} onClick={() => goTo(2)}>
              <span>Proceed to Spatial Scale</span>
              <Icon name="arrow_forward" size={18} />
            </button>
          </div>
        </div>

        {/* STEP 2 — scale */}
        <div ref={setStepRef(1)} hidden={done || step !== 2} className="flex flex-col gap-space-md">
          <StepHeading
            headingRef={setHeadingRef(1)}
            eyebrow="Metrics & Scope"
            title="Spatial Scale & Footprint"
            text="Slide to define your built-up carpet or super built-up interior footprint."
          />
          <div className="p-space-md sm:p-space-lg rounded bg-surface-cream flex flex-col items-center justify-center text-center shadow-inner">
            <label htmlFor={`${id}-area`} className="font-label-sm text-label-sm uppercase tracking-widest text-secondary font-bold">
              Estimated Square Footage
            </label>
            <output htmlFor={`${id}-area`} className="flex items-baseline gap-space-xs mt-1 mb-2">
              <span className="font-headline-xl text-headline-xl-mobile md:text-headline-xl text-primary font-medium tracking-tight">
                {formatInt(area)}
              </span>
              <span className="font-body-lg text-body-lg text-on-surface-variant">SQ.FT</span>
            </output>
            <div className="flex items-center gap-space-sm text-on-surface-variant font-body-sm text-body-sm">
              <Icon name="space_dashboard" size={18} className="text-emerald-vivid" />
              <span>{areaTier(area)}</span>
            </div>
          </div>
          <input type="hidden" name="scale" value={areaTier(area)} />
          <div className="flex flex-col gap-space-xs pt-space-xs">
            <input
              id={`${id}-area`}
              name="area_sqft"
              type="range"
              min={AREA.min}
              max={AREA.max}
              step={AREA.step}
              value={area}
              onChange={(e) => setArea(Number(e.target.value))}
              aria-valuetext={`${formatInt(area)} square feet`}
              className="w-full h-2 bg-surface-container-highest rounded-lg cursor-pointer accent-primary"
            />
            <div className="flex justify-between gap-2 text-on-surface-variant font-label-sm text-label-sm uppercase pt-1">
              <span>500 sq.ft (Studio / Boutique)</span>
              <span className="hidden sm:inline">5,000 sq.ft</span>
              <span className="text-right">15,000+ sq.ft (Estate Scale)</span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-space-xs pt-space-xs" role="group" aria-label="Square footage presets">
            <span className="font-label-sm text-label-sm text-on-surface-variant font-semibold uppercase mr-1">Presets:</span>
            {areaPresets.map((p) => (
              <button
                key={p.value}
                type="button"
                aria-pressed={area === p.value}
                onClick={() => setArea(p.value)}
                className={`px-space-sm py-1 rounded font-label-sm text-label-sm uppercase transition-colors ${
                  area === p.value ? 'bg-primary text-gold-light' : 'bg-surface-cream hover:bg-surface-container text-primary'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-space-sm pt-space-sm">
            <button type="button" className={prevBtn} onClick={() => goTo(1)}>
              <Icon name="arrow_back" size={18} />
              <span>Previous</span>
            </button>
            <button type="button" className={nextBtn} onClick={() => goTo(3)}>
              <span>Proceed to Budget &amp; Timeline</span>
              <Icon name="arrow_forward" size={18} />
            </button>
          </div>
        </div>

        {/* STEP 3 — timeline & budget */}
        <div ref={setStepRef(2)} hidden={done || step !== 3} className="flex flex-col gap-space-md">
          <StepHeading
            headingRef={setHeadingRef(2)}
            eyebrow="Planning & Capital"
            title="Target Timeline & Budgetary Spectrum"
            text="Align expectations to assist our quantity surveyor in structural allocation."
          />
          <fieldset>
            <legend className={legendCls}>Intended Handover Horizon</legend>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-space-xs">
              {timelines.map((t) => (
                <Choice key={t.value} name="timeline" value={t.value} defaultChecked={t.default} cardClassName="p-space-sm text-center font-body-sm text-body-sm h-full">
                  <span className="font-headline-sm text-[16px] leading-6 block">{t.title}</span>
                  <span className="sub text-on-surface-variant text-xs">{t.text}</span>
                </Choice>
              ))}
            </div>
          </fieldset>
          <fieldset className="pt-space-xs">
            <legend className={legendCls}>Planned Interior Investment Spectrum (INR)</legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-space-sm">
              {budgets.map((b) => (
                <Choice key={b.value} name="budget" value={b.value} defaultChecked={b.default} cardClassName="p-space-md flex flex-col h-full">
                  <span className="font-headline-sm text-[18px] leading-7">{b.title}</span>
                  <span className="sub font-body-sm text-body-sm text-on-surface-variant">{b.text}</span>
                </Choice>
              ))}
            </div>
          </fieldset>
          <div className="flex flex-wrap items-center justify-between gap-space-sm pt-space-sm">
            <button type="button" className={prevBtn} onClick={() => goTo(2)}>
              <Icon name="arrow_back" size={18} />
              <span>Previous</span>
            </button>
            <button type="button" className={nextBtn} onClick={() => goTo(4)}>
              <span>Proceed to Client Dossier</span>
              <Icon name="arrow_forward" size={18} />
            </button>
          </div>
        </div>

        {/* STEP 4 — contact details */}
        <div ref={setStepRef(3)} hidden={done || step !== 4} className="flex flex-col gap-space-md">
          <StepHeading
            headingRef={setHeadingRef(3)}
            eyebrow="Final Stage"
            title="Client Coordinates & Site Brief"
            text="Where may our principal studio dispatch your preliminary architectural appraisal?"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-space-md">
            <div className="flex flex-col gap-1">
              <label htmlFor={`${id}-name`} className={fieldLabel}>Full Name *</label>
              <input id={`${id}-name`} name="name" type="text" required autoComplete="name" placeholder="e.g. Dr. Anandhi Sundaram" className={field} />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor={`${id}-phone`} className={fieldLabel}>Phone Number (with WhatsApp) *</label>
              <div className="flex gap-2">
                <span aria-hidden="true" className="inline-flex items-center px-3 bg-surface-cream rounded text-primary font-body-md text-body-md font-medium select-none shadow-inner">
                  +91
                </span>
                <input
                  id={`${id}-phone`}
                  name="phone"
                  type="tel"
                  required
                  autoComplete="tel-national"
                  inputMode="tel"
                  placeholder="98400 12345"
                  className={field}
                />
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor={`${id}-email`} className={fieldLabel}>Email Address *</label>
              <input id={`${id}-email`} name="email" type="email" required autoComplete="email" placeholder="anandhi@domain.com" className={field} />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor={`${id}-location`} className={fieldLabel}>Site Location / City *</label>
              <input
                id={`${id}-location`}
                name="location"
                type="text"
                required
                autoComplete="address-level2"
                placeholder="e.g. Vadasery, Nagercoil / Kowdiar, Trivandrum"
                className={field}
              />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor={`${id}-message`} className={fieldLabel}>Project Vision &amp; Architectural Priorities</label>
            <textarea
              id={`${id}-message`}
              name="message"
              rows={3}
              placeholder="Tell us about your spatial aspirations (e.g. double-height living room, open-plan gourmet kitchen, minimalist pooja enclave, concealed acoustics)..."
              className={`${field} resize-y`}
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className={fieldLabel}>Architectural CAD / PDF Floor Plan (Optional)</span>
            <a
              href={whatsappPlan}
              target="_blank"
              rel="noopener noreferrer"
              className="p-space-md rounded bg-surface-cream flex flex-col items-center justify-center text-center transition-colors hover:bg-surface-container shadow-inner"
            >
              <Icon name="cloud_upload" size={32} className="text-secondary" />
              <span className="font-body-sm text-body-sm text-primary font-semibold mt-1">Share your blueprint documents on WhatsApp</span>
              <span className="font-label-sm text-label-sm text-on-surface-variant">PDF, DWG, PNG or JPEG · opens WhatsApp in a new tab</span>
            </a>
          </div>
          <div className="flex items-start gap-space-xs pt-space-xs">
            <input
              id={`${id}-consent`}
              name="consent"
              type="checkbox"
              value="Agreed to be contacted about this enquiry"
              required
              className="mt-1 accent-primary w-4 h-4 cursor-pointer shrink-0"
            />
            <label htmlFor={`${id}-consent`} className="font-body-sm text-body-sm text-on-surface-variant cursor-pointer">
              I authorize the Green Valley Interiors studio to review these details and contact me privately for feasibility
              scoping. My details are handled as described in the{' '}
              <AppLink href="/privacy-policy" className="underline underline-offset-2 text-primary hover:text-secondary">
                Privacy Policy
              </AppLink>
              .
            </label>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-space-sm pt-space-sm">
            <button type="button" className={`${prevBtn} order-2 sm:order-1`} onClick={() => goTo(3)}>
              <Icon name="arrow_back" size={18} />
              <span>Previous</span>
            </button>
            <button
              type="submit"
              disabled={status === 'submitting'}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-space-sm bg-primary text-gold-light hover:bg-primary-container px-space-lg lg:px-space-xl py-space-md rounded font-label-lg text-label-lg uppercase tracking-wider transition-all duration-300 shadow-xl hover:shadow-2xl hover:-translate-y-0.5 order-1 sm:order-2 disabled:opacity-70 text-center"
            >
              <Icon
                name={status === 'submitting' ? 'progress_activity' : 'send'}
                size={20}
                className={status === 'submitting' ? 'animate-spin' : ''}
              />
              <span>{status === 'submitting' ? 'Sending…' : 'Submit & Request Preliminary Architectural Scoping'}</span>
            </button>
          </div>
          <FormStatusMessage status={status} error={error} successText="" />
        </div>

        {/* SUCCESS */}
        <div
          ref={successRef}
          tabIndex={-1}
          hidden={!done}
          className="p-space-md sm:p-space-lg rounded bg-primary text-on-primary flex flex-col items-center text-center gap-space-sm shadow-xl focus:outline-none"
        >
          <div className="w-16 h-16 rounded-full bg-emerald-vivid text-gold-light flex items-center justify-center">
            <Icon name="done_all" size={36} />
          </div>
          <h3 className="font-headline-md text-headline-md text-gold-light">Dossier Received by Senior Partners</h3>
          <p className="font-body-md text-body-md text-surface-container-high max-w-lg">
            Thank you. Your project brief has been allocated to our Chief Estimator &amp; Spatial Architect. We will reach out
            via WhatsApp / phone within 24 operational hours with a scoping framework.
          </p>
          <div className="pt-space-xs flex flex-col items-center gap-space-sm">
            <a
              href={`tel:${SITE.phoneE164}`}
              className="inline-flex items-center gap-space-xs font-label-md text-label-md uppercase tracking-wider text-gold-light underline underline-offset-4"
            >
              Need priority dispatch? Call {SITE.phoneDisplay}
            </a>
            <button type="button" onClick={startOver} className="font-label-sm text-label-sm uppercase tracking-wider text-surface-ivory/70 hover:text-gold-light">
              Send another brief
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
